export const compactNumberFormatter = (value: number): string => {
  if (value === undefined || value === null) return '0'

  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`
  }
  if (Math.abs(value) >= 1_000) {
    return `${(value / 1_000).toFixed(0)}K`
  }
  return value.toString()
}

export const currencyFormatter = (value: number, compact = true): string => {
  if (value === undefined || value === null) return '$0'

  if (compact) {
    return `$${compactNumberFormatter(value)}`
  }

  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

export const localeNumberFormatter = (value: number, decimals = 0): string => {
  if (value === undefined || value === null) return '0'

  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
}

export const currencyTooltipFormatter = (value: number, decimals = 0): string => {
  const formatted = localeNumberFormatter(value, decimals)
  return `$${formatted}`
}

export const signedCurrencyFormatter = (value: number, decimals = 2): string => {
  const formatted = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
  const prefix = value < 0 ? '-' : ''
  return `${prefix}$${formatted}`
}

export const parseMonthDate = (dateString: string | undefined): Date | null => {
  if (!dateString) return null
  const [year, month] = dateString.split('-').map(Number)
  return new Date(year, month - 1, 1)
}

export const monthYearFormatter = (dateString: string | undefined): string => {
  if (!dateString) return ''
  const date = parseMonthDate(dateString)
  if (!date) return ''
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

export const shortMonthYearFormatter = (value: string): string => {
  if (!value || typeof value !== 'string') return ''

  const parts = value.split(' ')
  if (parts.length < 2) return value

  const [month, year] = parts
  return `${month} ${year.slice(2)}`
}

export const fullDateFormatter = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

export const longDateFormatter = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

export const createYAxisFormatter = (isEconomics: boolean) => {
  return (value: number): string => {
    if (value === undefined || value === null) return isEconomics ? '$0' : '0'
    const formatted = compactNumberFormatter(value)
    return isEconomics ? `$${formatted}` : formatted
  }
}

export const createTooltipValueFormatter = (isEconomics: boolean) => {
  return (value: number): string => {
    const formatted = localeNumberFormatter(value)
    return isEconomics ? `$${formatted}` : formatted
  }
}

/**
 * Formats a date string "YYYY-MM-DD" as "Feb 18" (short month + day).
 * Used for daily resolution chart/table labels.
 */
export const dayFormatter = (dateString: string): string => {
  if (!dateString) return ''
  const date = new Date(`${dateString}T00:00:00`)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Formats a week-start date string "YYYY-MM-DD" as "Feb 10 – 16".
 * Shows the Sunday–Saturday range. If the week spans months, shows "Feb 10 – Mar 1".
 */
export const weekFormatter = (weekStartStr: string): string => {
  if (!weekStartStr) return ''
  const start = new Date(`${weekStartStr}T00:00:00`)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)

  const startMonth = start.toLocaleDateString('en-US', { month: 'short' })
  const startDay = start.getDate()
  const endDay = end.getDate()

  if (start.getMonth() === end.getMonth()) {
    return `${startMonth} ${startDay}–${endDay}`
  }
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' })
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}`
}

/**
 * Returns the appropriate category formatter based on the grouping.
 */
export const getFormatterForGrouping = (grouping: 'daily' | 'weekly' | 'monthly'): ((dateStr: string) => string) => {
  switch (grouping) {
    case 'daily':
      return dayFormatter
    case 'weekly':
      return weekFormatter
    case 'monthly':
      return monthYearFormatter
  }
}
